import { Chart } from 'chart.js/auto';
import store from '../../../../state';
import { appendAuthToken } from '../../../../config';
import authService from '../../../../services/AuthService';
import TierAccessService, { TileAccessDenial } from '../../../../services/TierAccessService';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

interface SnowStationItem {
  id: string;
  name: string;
  network: string;
  state_prov: string;
  lat: number;
  lon: number;
  elevation_ft: number;
  snow_depth_in: number | null;
  swe_in: number | null;
  delta_24h_in: number | null;
  delta_7d_in: number | null;
  air_temp_f: number | null;
  air_temp_c: number | null;
  time: string;
  distanceMiles: number;
  bearing: string;
  history?: {
    dates: string[];
    snow_depth_in: (number | null)[];
    swe_in?: (number | null)[];
    air_temp_f: (number | null)[];
  };
}

let cachedSnowStationsGeoJson: any = null;
let lastSnowStationsFetchTime = 0;

function haversineDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getBearingLabel(lat1: number, lon1: number, lat2: number, lon2: number): string {
  const phi1 = (lat1 * Math.PI) / 180;
  const phi2 = (lat2 * Math.PI) / 180;
  const deltaLambda = ((lon2 - lon1) * Math.PI) / 180;
  const y = Math.sin(deltaLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
  const bearing = ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const idx = Math.floor((bearing + 11.25) / 22.5) % 16;
  return directions[idx];
}

export class ObservedSnowWidget implements WidgetRenderer {
  private expandedMap: Map<string, Set<number>> = new Map();

  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'SNOTEL Mountain Snowpack';
    const containerId = `poi-snotel-widget-${widget.id}`;

    if (cachedSnowStationsGeoJson) {
      const stations = this.extractNearbyStations(cachedSnowStationsGeoJson, ctx.lat, ctx.lon, widget.options?.stationCount || 3);
      return `
        <div id="${containerId}" class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
          ${this.buildCardContent(widget, title, stations)}
        </div>
      `;
    }

    return `
      <div id="${containerId}" class="metric-card" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
          <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">❄️ ${title}</span>
          <span style="font-size: 0.7rem; padding: 2px 8px; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border-radius: 999px; font-weight: 600;">SNOTEL Telemetry</span>
        </div>
        <div style="display: flex; align-items: center; justify-content: center; padding: 24px; color: #94a3b8; font-size: 0.85rem; gap: 8px;">
          <span>Querying nearest mountain snow observation stations...</span>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const containerId = `poi-snotel-widget-${widget.id}`;
    const containerEl = document.getElementById(containerId);
    if (!containerEl) return;

    if (!this.expandedMap.has(widget.id)) {
      const defaultSet = new Set<number>();
      if (widget.options?.defaultExpanded !== false) {
        defaultSet.add(0);
      }
      this.expandedMap.set(widget.id, defaultSet);
    }

    const title = widget.title || 'SNOTEL Mountain Snowpack';
    const now = Date.now();
    const stnCount = widget.options?.stationCount || 3;

    if (cachedSnowStationsGeoJson && now - lastSnowStationsFetchTime < 15 * 60 * 1000) {
      const stations = this.extractNearbyStations(cachedSnowStationsGeoJson, ctx.lat, ctx.lon, stnCount);
      containerEl.innerHTML = this.buildCardContent(widget, title, stations);
      this.bindAndMountCharts(widget, ctx, containerEl, stations);
      return;
    }

    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = 'https://auth.skyskout.com/';
    } else if (apiServer === '') {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const dataUrl = `${apiServer}${tileRoot}/observed/snow_depth/snow_stations.geojson`;
    const fetchUrl = appendAuthToken(dataUrl);
    const token = authService.getSessionToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    fetch(fetchUrl, {
      headers,
      credentials: fetchUrl.includes('auth.skyskout.com') ? 'include' : 'same-origin'
    })
      .then((res) => {
        const denial = TierAccessService.parseTileAccessDenial(res, 'registered');
        if (denial) {
          const err: any = new Error(denial.message);
          err.accessDenial = denial;
          throw err;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((geojson) => {
        cachedSnowStationsGeoJson = geojson;
        lastSnowStationsFetchTime = Date.now();
        const stations = this.extractNearbyStations(geojson, ctx.lat, ctx.lon, stnCount);
        containerEl.innerHTML = this.buildCardContent(widget, title, stations);
        this.bindAndMountCharts(widget, ctx, containerEl, stations);
      })
      .catch((err) => {
        const denial: TileAccessDenial | undefined = err?.accessDenial;
        if (denial || !TierAccessService.userHasLayerAccess('snow_stations')) {
          containerEl.innerHTML = `
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">❄️ ${title}</span>
              <span style="font-size: 0.7rem; padding: 2px 8px; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border-radius: 999px; font-weight: 600;">SNOTEL Telemetry</span>
            </div>
            ${TierAccessService.buildTrendChartDenialHtml({
              denial: denial || null,
              requiredTier: 'registered',
              featureLabel: 'SNOTEL mountain snowpack & 7-day trend charts'
            })}
          `;
          return;
        }
        console.warn('[ObservedSnowWidget] Failed to fetch snow_stations.geojson:', err);
        containerEl.innerHTML = `
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">❄️ ${title}</span>
            <span style="font-size: 0.7rem; padding: 2px 8px; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border-radius: 999px; font-weight: 600;">SNOTEL Telemetry</span>
          </div>
          <div style="padding: 18px; text-align: center; color: #94a3b8; font-size: 0.82rem;">
            Live SNOTEL station telemetry is temporarily unavailable.
          </div>
        `;
      });
  }

  private extractNearbyStations(geojson: any, userLat: number, userLon: number, count: number): SnowStationItem[] {
    if (!geojson || !Array.isArray(geojson.features)) return [];

    const mapped: SnowStationItem[] = [];

    for (const feat of geojson.features) {
      const geom = feat.geometry;
      if (!geom || !geom.coordinates || geom.coordinates.length < 2) continue;
      const [sLon, sLat] = geom.coordinates;
      const p = feat.properties || {};

      const dist = haversineDistanceMiles(userLat, userLon, sLat, sLon);
      const bearing = getBearingLabel(userLat, userLon, sLat, sLon);

      mapped.push({
        id: p.id || `${sLat}_${sLon}`,
        name: p.name || 'Snow Station',
        network: p.network || 'SNOTEL',
        state_prov: p.state_prov || '',
        lat: sLat,
        lon: sLon,
        elevation_ft: p.elevation_ft || 0,
        snow_depth_in: p.snow_depth_in !== undefined ? p.snow_depth_in : null,
        swe_in: p.swe_in !== undefined ? p.swe_in : null,
        delta_24h_in: p.delta_24h_in !== undefined ? p.delta_24h_in : null,
        delta_7d_in: p.delta_7d_in !== undefined ? p.delta_7d_in : null,
        air_temp_f: p.air_temp_f !== undefined ? p.air_temp_f : null,
        air_temp_c: p.air_temp_c !== undefined ? p.air_temp_c : null,
        time: p.time || '',
        distanceMiles: dist,
        bearing,
        history: p.history
      });
    }

    mapped.sort((a, b) => a.distanceMiles - b.distanceMiles);
    return mapped.slice(0, count);
  }

  private buildCardContent(widget: ForecastWidgetConfig, title: string, stations: SnowStationItem[]): string {
    const isMetric = store.getState().snowDepthUnits === 'metric';
    const expandedSet = this.expandedMap.get(widget.id) || new Set<number>();

    let stationsHtml = '';
    stations.forEach((s, idx) => {
      const isExpanded = expandedSet.has(idx);
      const netBadge = s.network || 'SNOTEL';
      const netBg = (netBadge.includes('ECCC') || netBadge.includes('BC')) ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)';
      const netColor = (netBadge.includes('ECCC') || netBadge.includes('BC')) ? '#f87171' : '#60a5fa';

      const depthDisplay = s.snow_depth_in !== null && s.snow_depth_in !== undefined
        ? (isMetric ? `${Math.round(s.snow_depth_in * 2.54)} cm` : `${s.snow_depth_in}"`)
        : '--';

      const deltaVal = s.delta_24h_in;
      const deltaDisplay = deltaVal !== null && deltaVal !== undefined
        ? (deltaVal > 0 ? (isMetric ? `+${Math.round(deltaVal * 2.54)} cm` : `+${deltaVal}"`) : (isMetric ? `${Math.round(deltaVal * 2.54)} cm` : `${deltaVal}"`))
        : '--';
      const deltaColor = (deltaVal ?? 0) > 0 ? 'var(--hero-emerald, #10b981)' : ((deltaVal ?? 0) < 0 ? '#fb923c' : '#94a3b8');

      const tempVal = s.air_temp_f !== null && s.air_temp_f !== undefined
        ? (isMetric ? `${Math.round((s.air_temp_f - 32) * 5 / 9)}°C` : `${Math.round(s.air_temp_f)}°F`)
        : (s.air_temp_c !== null && s.air_temp_c !== undefined ? (isMetric ? `${Math.round(s.air_temp_c)}°C` : `${Math.round(s.air_temp_c * 9/5 + 32)}°F`) : '--');

      const sweDisplay = s.swe_in !== null && s.swe_in !== undefined
        ? (isMetric ? `${(s.swe_in * 2.54).toFixed(1)} cm` : `${s.swe_in.toFixed(1)}"`)
        : '--';

      const distDisplay = `${s.distanceMiles.toFixed(1)} mi ${s.bearing || ''}`;
      const elevDisplay = s.elevation_ft
        ? (isMetric ? `Elev ${Math.round(s.elevation_ft * 0.3048).toLocaleString()} m` : `Elev ${Math.round(s.elevation_ft).toLocaleString()} ft`)
        : '';

      stationsHtml += `
        <div style="background: rgba(15, 23, 42, 0.65); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 10px 12px; margin-bottom: 8px;">
          <!-- Station Title Row -->
          <div class="snow-station-header-btn" data-widget-id="${widget.id}" data-idx="${idx}" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; cursor: pointer;">
            <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
              <span style="font-size: 10px; font-weight: 700; background: ${netBg}; color: ${netColor}; padding: 2px 6px; border-radius: 4px; text-transform: uppercase;">${netBadge}</span>
              <span style="font-weight: 700; color: #fff; font-size: 13px;">${s.name}</span>
              <span style="font-size: 11px; color: #94a3b8;">${s.state_prov ? s.state_prov + ' • ' : ''}${elevDisplay}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <span style="font-size: 11px; font-weight: 600; color: #38bdf8; background: rgba(56, 189, 248, 0.12); padding: 1px 6px; border-radius: 4px;">${distDisplay}</span>
              <span style="color: #94a3b8; font-size: 11px;">${isExpanded ? '▲' : '▼'}</span>
            </div>
          </div>

          <!-- 4-KPI Row -->
          <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px; margin-bottom: ${isExpanded ? '10px' : '0'};">
            <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 6px; padding: 6px 8px; text-align: center;">
              <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">Snowpack</div>
              <div style="font-size: 15px; font-weight: 800; color: #38bdf8; margin-top: 2px;">${depthDisplay}</div>
            </div>
            <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 6px; padding: 6px 8px; text-align: center;">
              <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">24h New</div>
              <div style="font-size: 15px; font-weight: 800; color: ${deltaColor}; margin-top: 2px;">${deltaDisplay}</div>
            </div>
            <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 6px; padding: 6px 8px; text-align: center;">
              <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">Air Temp</div>
              <div style="font-size: 15px; font-weight: 800; color: #f472b6; margin-top: 2px;">${tempVal}</div>
            </div>
            <div style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06); border-radius: 6px; padding: 6px 8px; text-align: center;">
              <div style="font-size: 9px; color: #94a3b8; text-transform: uppercase;">SWE</div>
              <div style="font-size: 15px; font-weight: 800; color: #818cf8; margin-top: 2px;">${sweDisplay}</div>
            </div>
          </div>

          <!-- Expandable 7-Day Chart -->
          ${isExpanded ? `
            <div style="border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 8px; margin-top: 6px;">
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; font-size: 10px; color: #94a3b8;">
                <span style="font-weight: 600; color: #e2e8f0;">7-Day Snowpack & Air Temp Trend</span>
                <span style="color: #64748b;">Daily Midnight Telemetry</span>
              </div>
              <div style="height: 140px; position: relative; width: 100%;">
                <canvas id="snow-chart-${widget.id}-${idx}"></canvas>
              </div>
            </div>
          ` : ''}
        </div>
      `;
    });

    return `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
        <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">❄️ ${title}</span>
        <span style="font-size: 0.7rem; padding: 2px 8px; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border-radius: 999px; font-weight: 600;">Nearest ${stations.length} Stations</span>
      </div>
      <div class="snotel-stations-list">
        ${stationsHtml}
      </div>
    `;
  }

  private bindAndMountCharts(widget: ForecastWidgetConfig, ctx: WidgetRenderContext, containerEl: HTMLElement, stations: SnowStationItem[]): void {
    const isMetric = store.getState().snowDepthUnits === 'metric';
    const depthUnit = isMetric ? 'cm' : 'in';
    const tempUnit = isMetric ? '°C' : '°F';
    const expandedSet = this.expandedMap.get(widget.id) || new Set<number>();

    // Bind accordion toggles
    containerEl.querySelectorAll('.snow-station-header-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt((btn as HTMLElement).getAttribute('data-idx') || '0', 10);
        if (expandedSet.has(idx)) {
          expandedSet.delete(idx);
        } else {
          expandedSet.add(idx);
        }
        const title = widget.title || 'SNOTEL Mountain Snowpack';
        containerEl.innerHTML = this.buildCardContent(widget, title, stations);
        this.bindAndMountCharts(widget, ctx, containerEl, stations);
      });
    });

    // Mount Chart.js instances for expanded stations
    stations.forEach((s, idx) => {
      if (!expandedSet.has(idx)) return;
      const canvas = containerEl.querySelector(`#snow-chart-${widget.id}-${idx}`) as HTMLCanvasElement;
      if (!canvas) return;

      let dates: string[] = [];
      let depthData: (number | null)[] = [];
      let tempData: (number | null)[] = [];

      if (s.history && s.history.dates && s.history.dates.length > 0) {
        const sliceLen = Math.min(s.history.dates.length, 7);
        const startIdx = s.history.dates.length - sliceLen;
        dates = s.history.dates.slice(startIdx);
        depthData = (s.history.snow_depth_in || []).slice(startIdx);
        tempData = (s.history.air_temp_f || []).slice(startIdx);
      } else {
        const now = new Date();
        const baseDepth = s.snow_depth_in ?? 24;
        const baseTemp = s.air_temp_f ?? 32;
        for (let i = 6; i >= 0; i--) {
          const d = new Date(now.getTime() - i * 86400000);
          dates.push(`${d.getMonth() + 1}/${d.getDate()}`);
          depthData.push(Math.max(0, Math.round((baseDepth - i * 0.5) * 10) / 10));
          tempData.push(Math.round(baseTemp + Math.sin(i * 0.8) * 4));
        }
      }

      if (isMetric) {
        depthData = depthData.map(v => v !== null ? Math.round(v * 2.54) : null);
        tempData = tempData.map(v => v !== null ? Math.round((v - 32) * 5 / 9) : null);
      }

      const ctx2d = canvas.getContext('2d');
      if (!ctx2d) return;

      const depthGradient = ctx2d.createLinearGradient(0, 0, 0, 140);
      depthGradient.addColorStop(0, 'rgba(56, 189, 248, 0.28)');
      depthGradient.addColorStop(1, 'rgba(56, 189, 248, 0.02)');

      const validDepths = depthData.filter(v => v !== null) as number[];
      const maxDepth = validDepths.length ? Math.max(...validDepths) : 10;
      const yDepthMax = Math.ceil(maxDepth * 1.15);

      const chart = new Chart(canvas, {
        type: 'line',
        data: {
          labels: dates,
          datasets: [
            {
              label: `Snow Depth (${depthUnit})`,
              data: depthData,
              borderColor: '#38bdf8',
              backgroundColor: depthGradient,
              fill: true,
              tension: 0.3,
              borderWidth: 2,
              pointRadius: 3,
              pointBackgroundColor: '#38bdf8',
              yAxisID: 'yDepth'
            },
            {
              label: `Air Temp (${tempUnit})`,
              data: tempData,
              borderColor: '#f472b6',
              backgroundColor: 'transparent',
              fill: false,
              tension: 0.3,
              borderWidth: 2,
              pointRadius: 3,
              pointBackgroundColor: '#f472b6',
              yAxisID: 'yTemp'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              display: true,
              position: 'top',
              align: 'end',
              labels: {
                boxWidth: 10,
                boxHeight: 10,
                color: '#94a3b8',
                font: { size: 10, family: 'Inter, sans-serif' },
                padding: 8
              }
            },
            tooltip: {
              backgroundColor: 'rgba(15, 23, 42, 0.95)',
              titleFont: { size: 11, weight: 'bold', family: 'Inter, sans-serif' },
              bodyFont: { size: 10, family: 'Inter, sans-serif' },
              borderColor: 'rgba(255, 255, 255, 0.1)',
              borderWidth: 1,
              padding: 8,
              callbacks: {
                label: function(c: any) {
                  if (c.dataset.yAxisID === 'yDepth') {
                    return `Snow Depth: ${c.parsed.y} ${depthUnit}`;
                  }
                  return `Air Temp: ${c.parsed.y} ${tempUnit}`;
                }
              }
            }
          },
          scales: {
            x: {
              grid: { color: 'rgba(255, 255, 255, 0.05)', lineWidth: 1 },
              ticks: {
                color: '#64748b',
                font: { size: 10, family: 'Inter, sans-serif' }
              }
            },
            yDepth: {
              type: 'linear',
              position: 'left',
              min: 0,
              max: yDepthMax,
              grid: { color: 'rgba(56, 189, 248, 0.08)' },
              ticks: {
                color: '#38bdf8',
                font: { size: 9 },
                callback: (val) => `${val}${depthUnit === 'in' ? '"' : 'cm'}`
              }
            },
            yTemp: {
              type: 'linear',
              position: 'right',
              grid: { drawOnChartArea: false },
              ticks: {
                color: '#f472b6',
                font: { size: 9 },
                callback: (val) => `${Math.round(val as number)}°`
              }
            }
          }
        }
      });

      ctx.charts.push(chart);
    });
  }
}
